import React, { useState, useEffect } from "react";
import { VsiH4, VsiH5, VsiHr, VsiLgText, VsiMedLabel } from "../../styled/App.styled";
import {
  Row,
  Col,
  Label,
  Input,
  Button,
  FormGroup,
  FormFeedback,
  Spinner,
  Accordion,
  AccordionItem,
  Badge,
  AccordionHeader,
  AccordionBody
} from "reactstrap";
import { useVsiAppContext } from "../../stores/VsiApp";
import { DeleteAuth, GetAuth, PutAuth } from "../../utils/Verbs";

import { menuItems, utilItems } from "../../utils/routes";
import themeStyles from "../../utils/themeStyles";
import { returnRootContextWithContext, snakeCaseToTitleCase, toSnakeCase } from "../../utils/Helpers";
import swal from "sweetalert";
import { useDebounce } from "../../utils/hooks";

const badgeStyle = {
  cursor: "pointer"
};

const defaultIcon = "fa-chart-line";

export default function Profile() {
  const { theme, user, getAccessTokenSilently, setUser } = useVsiAppContext();
  const allSideItems = [
    ...menuItems,
    ...utilItems
  ];

  const sideItemsContextIconMap = allSideItems.reduce((acc, cumm) => {
    return {
      ...acc,
      [cumm.context]: cumm.icon
    };
  }, {});

  const [isSuccessful, setIsSuccessful] = useState(false);
  const [isPutting, setIsPutting] = useState(false);
  const [error, setError] = useState(null);
  const [invalid, setInvalid] = useState(false);
  const domain = user.email.split("@");

  const [firstName, setFirstName] = useState(user.firstName || "");
  const [lastName, setLastName] = useState(user.lastName || "");
  const [email, setEmail] = useState(user.email || "");
  const [persistClient, setPersistClient] = useState(user.persistClient || false);

  const [accordianOpen, setAccordianOpen] = useState(null);
  const debouncePersistClientCheck = useDebounce(persistClient, 250);

  const { columnsByContext, keyedColumnsByContext } = user;

  useEffect(() => {
    setFirstName(user.firstName);
    setLastName(user.lastName);
    setPersistClient(user.persistClient);
  }, [user]);

  useEffect(() => {
    if (debouncePersistClientCheck === persistClient && persistClient !== user.persistClient) {
      saveUserInfo();
    }
  }, [debouncePersistClientCheck, persistClient]);


  const saveUserInfo = async function() {
    if (!lastName || !firstName || !email) {
      return;
    }
    const route = "/api/v6/admin/profile/user/" + user._id;

    try {
      let activeClientUpdate;
      if (!persistClient) {
        activeClientUpdate = null;
      }
      const token = await getAccessTokenSilently();
      const data = await PutAuth(route, {
        firstName,
        lastName,
        email,
        persistClient,
        activeClient: activeClientUpdate
      }, token);
      if (data.status === 200) {
        if (email !== user.email) {
          var userData = { ...data.data.data_updated, token: token };
        } else {
          userData = { ...data.data.data_updated, clientId: user.clientId, token: token };
        }
        // Returns the client's mongoID
        let clientData;
        if (!persistClient && user.role === "super_user") {
          let defaultClientData = await GetAuth(`/api/v6/admin/clients/mongo/${data.data.data_updated?.client?._id}`, token);
          clientData = defaultClientData.data;
        } else {
          clientData = user.client;
        }
        setUser(user => {
          return {
            ...user,
            ...userData,
            client: clientData
          };
        });
        setIsSuccessful(true);
        setIsPutting(false);
        return;
      }
      throw new Error("Unsuccessful status code");
    } catch (err) {
      setError(err);
      setIsSuccessful(false);
      setIsPutting(false);
      console.error(err);
    }
  };

  const openAccordian = id => {
    if (id === accordianOpen) {
      setAccordianOpen(null);
    } else {
      setAccordianOpen(id);
    }
  };
  //
  const eraseSavedConfig = async (route) => {
    const { name, title, params, _id } = route;

    const keyedParams = Object.keys(params ?? {});
    const spec = keyedParams.length > 0 ? keyedParams.reduce((acc, cumm) => acc + `${snakeCaseToTitleCase(cumm)}: ${snakeCaseToTitleCase(params[cumm])} `, "") : " ";
    const deleteConfirmed = await swal({
      title: `Delete ${name ? name : `${title} (${spec})`} configuration?`,
      text: "These grid column configurations will be unrecoverable.",
      icon: "warning",
      buttons: true,
      dangerMode: true
    });
    if (deleteConfirmed) {
      try {
        const token = await getAccessTokenSilently();
        await DeleteAuth(`/api/v6/admin/user_col_config/${user._id}/${_id}`, token);
        let newConfig = user.userColConfig.filter(config => config._id !== _id);
        setUser(user => {
          return {
            ...user,
            userColConfig: newConfig
          };
        });
      } catch (e) {
        console.error(e);
      }
    }
  };

  return <div>
    <VsiH4>Profile</VsiH4>
    <VsiHr />
    <Row>
      <Col lg={6} md={12} className={"mb-4"}>
        <VsiH5>Details</VsiH5>
        <FormGroup>
          <Label for="firstName" className={`label-${theme}`}>First Name</Label>
          <Input placeholder={"First Name"}
                 invalid={!firstName}
                 disabled={true}
                 id="firstName"
                 value={firstName} />
        </FormGroup>
        <FormGroup>
          <Label for="lastName" className={`label-${theme}`}>Last Name</Label>
          <Input placeholder={"Last Name"}
                 id="lastName"
                 value={lastName}
                 invalid={!lastName}
                 disabled={true} />
        </FormGroup>
        <FormGroup>
          <Label for="email" className={`label-${theme}`}>Email</Label>
          <Input
            placeholder={"Email"}
            id="email"
            defaultValue={`${domain[0]}@${domain[1]}`}
            invalid={!email}
            disabled={true}
          />
        </FormGroup>
        {user.role === "super_user" ? (
          <FormGroup>
            {isPutting ? <Spinner size={"sm"} color={"success"}/> : (
              <Input className="me-2"
                     type="checkbox"
                     name="persistClient"
                     checked={persistClient}
                     onChange={() => {
                       setIsPutting(true);
                       setPersistClient(!persistClient);
                     }} />
            )}
            <Label>
              Persist Client on Refresh
            </Label>
          </FormGroup>
        ) : null}
      </Col>
      <Col lg={6} md={12}>
        <VsiH5>Grid Column Configurations</VsiH5>
        <Row>
          {(keyedColumnsByContext ?? []).map((context, index) => {
            const contextColumns = columnsByContext[context];
            const rootContext = returnRootContextWithContext(context);
            const keyedColumns = Object.keys(contextColumns);
            const accId = `context-accordian-${toSnakeCase(context)}`;
            return (
              <Col xs={12} key={`key-${accId}`}>
                <Accordion flush className={`bg-${theme}`} toggle={openAccordian} open={accordianOpen} stayOpen>
                  <AccordionItem className={`bg-${theme} mb-1`}>
                    <AccordionHeader targetId={`${index + 1}`} className={`bg-${theme} accordian-header-${theme}`}>
                      <VsiLgText>
                        <i
                          className={`fa-solid ${sideItemsContextIconMap[rootContext] || defaultIcon} px-2`}
                          style={{ color: themeStyles[theme].sideColor }} />
                        {context}
                      </VsiLgText>
                    </AccordionHeader>
                    <AccordionBody accordionId={`${index + 1}`} className={`bg-${theme}`}>
                      {(keyedColumns ?? []).map(pathKey => {
                        const columnInfo = columnsByContext?.[context]?.[pathKey] ?? {};
                        return (
                          <div key={`column-report-${pathKey.replace(/[\/\?&]/g, "-")}`}
                               style={{ overflowX: "auto" }}
                               className={"d-flex flex-row align-items-center mb-2"}>
                            <i
                              className={`fa-solid ${columnInfo.icon || defaultIcon} px-2`}
                              style={{ color: themeStyles[theme].sideColor }} />
                            <VsiLgText className={"me-2"}>{columnInfo?.title ?? "N/A"}</VsiLgText>
                            <div className={"d-flex flex-row"}>
                              {(columnInfo?.columns ?? []).map(col => {
                                const params = col.params;
                                const keyedParams = Object.keys(params ?? {});
                                let specStr = col.name || "";
                                let keyStr = "none";
                                let addtilStr = "";
                                let addtlKey = "";
                                if (!col.name && keyedParams.length > 0) {
                                  keyedParams.forEach((key, index) => {
                                    addtilStr += `${snakeCaseToTitleCase(key)}: ${snakeCaseToTitleCase(params[key])} `;
                                    specStr = addtilStr;
                                    addtlKey += `${key}-${params[key]}`;
                                    keyStr = addtlKey;
                                  });
                                }

                                return (
                                  <div className={"me-2 d-flex flex-column"}
                                       key={`report-param-${specStr.replace(/[\/\?&]/g, "-")}-key-${keyStr}`}>
                                    <Badge color={"danger"} style={badgeStyle} onClick={() => eraseSavedConfig({
                                      title: columnInfo.title,
                                      ...col
                                    })}>
                                      {specStr}
                                      <i className="fa-solid fa-eraser" />
                                    </Badge>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </AccordionBody>
                  </AccordionItem>
                </Accordion>
              </Col>
            );
          })}
        </Row>
      </Col>
    </Row>
  </div>;
}
